import gql from "graphql-tag";
import { Dictionary } from "lodash";
import {
  Article,
  ArticleCategory,
  Article_InventoryType,
  Distributor,
  Footprint,
  Location,
  ProductGroup,
} from "../../../api/schema";

export interface ArticleListResponse {
  articles: Array<Article>;
  locations: Array<Location>;
  footprints: Array<Footprint>;
  articleCategories: Array<ArticleCategory>;
  productGroups: Array<ProductGroup>;
}

export interface ArticleViewResponse {
  article: Article;
  distributors: Array<Distributor>;
  locations: Array<Location>;
  footprints: Array<Footprint>;
  productGroups: Array<ProductGroup>;
  articleCategories: Array<ArticleCategory>;
}

export interface CreateArticleState {
  name: string;
  location: Location;
  footprint: Footprint;
  article_category: ArticleCategory;
  inventory_type: Article_InventoryType;
  fields: Dictionary<String>;
  picture: string | null;
  datasheet_url: string | null;
}

export interface CreateArticleResponse {
  createArticle: { id: number };
}

export interface UpdateArticleResponse {
  updateArticle: { id: number };
}

export interface DeleteArticleResponse {
  deleteArticle: boolean;
}

export const ArticleViewQuery = gql`
  query articleViewQuery($id: ID!) {
    article(id: $id) {
      id
      name
      matchcode
      inventory_type
      display_name
      picture
      fields {
        id
        value
        suffix
        key_field {
          id
          name
          type
          values
          required
          unit
        }
      }
      datasheet_url
      price_information {
        id
        tier
        prices {
          id
          quantity
          price
        }
      }
      footprint {
        id
        name
      }
      location {
        id
        name
        parent {
          id
          name
        }
      }
      article_category {
        id
        name
        fields {
          id
          values
          required
          type
          unit
          name
          created_at
          updated_at
        }
      }
      product_group {
        id
        name
        parent {
          id
          name
          parent {
            id
            name
            parent {
              id
              name
            }
          }
        }
      }
      sources {
        id
        order_number
        url
        moq
        quantity_scale
        distributor {
          id
          name
          short_name
          search_url
        }
      }
    }
    footprints {
      id
      name
    }
    articleCategories {
      id
      name
      fields {
        id
        values
        required
        type
        unit
        name
        created_at
        updated_at
      }
    }
    productGroups {
      id
      name
      parent {
        id
        name
        parent {
          id
          name
          parent {
            id
            name
          }
        }
      }
      created_at
      updated_at
    }
    distributors {
      id
      name
      short_name
      search_url
      created_at
      updated_at
    }
    locations {
      id
      name
      parent {
        id
        name
        parent {
          id
          name
        }
      }
    }
  }
`;

export const ArticleListQuery = gql`
  query articleListViewQuery {
    articles {
      id
      name
      matchcode
      inventory_type
      picture
      display_name
      datasheet_url
      footprint {
        id
        name
      }
      location {
        id
        name
        parent {
          id
          name
        }
      }
      article_category {
        id
        name
        fields {
          id
          values
          required
          type
          unit
          name
          created_at
          updated_at
        }
      }
      product_group {
        id
        name
        parent {
          id
          name
        }
      }
    }
    footprints {
      id
      name
    }
    articleCategories {
      id
      name
      fields {
        id
        values
        required
        type
        unit
        name
        created_at
        updated_at
      }
    }
    productGroups {
      id
      name
      parent {
        id
        name
        parent {
          id
          name
          parent {
            id
            name
          }
        }
      }
      created_at
      updated_at
    }
    locations {
      id
      name
      parent {
        id
        name
        parent {
          id
          name
        }
      }
    }
  }
`;

export const CreateArticleMutation = gql`
  mutation createArticle($values: CreateArticleInput!) {
    createArticle(values: $values) {
      id
    }
  }
`;

export const UpdateArticleMutation = gql`
  mutation updateArticle($values: UpdateArticleInput!) {
    updateArticle(values: $values) {
      id
    }
  }
`;

export const DeleteArticleMutation = gql`
  mutation deleteArticle($id: ID!) {
    deleteArticle(id: $id)
  }
`;

export const CreateArticlePriceMutation = gql`
  mutation createArticlePrice($values: CreateArticlePriceInput!) {
    createArticlePrice(values: $values) {
      id
    }
  }
`;

export const UpdateArticlePriceMutation = gql`
  mutation updateArticlePrice($values: UpdateArticlePriceInput!) {
    updateArticlePrice(values: $values) {
      id
    }
  }
`;

export const DeleteArticlePriceMutation = gql`
  mutation deleteArticlePrice($id: ID!) {
    deleteArticlePrice(id: $id)
  }
`;

export const CreateArticlePriceTierMutation = gql`
  mutation createArticlePriceTier($values: CreateArticlePriceTierInput!) {
    createArticlePriceTier(values: $values) {
      id
    }
  }
`;

export const UpdateArticlePriceTierMutation = gql`
  mutation updateArticlePriceTier($values: UpdateArticlePriceTierInput!) {
    updateArticlePriceTier(values: $values) {
      id
    }
  }
`;

export const DeleteArticlePriceTierMutation = gql`
  mutation deleteArticlePriceTier($id: ID!) {
    deleteArticlePriceTier(id: $id)
  }
`;

export const CreateArticleParameterMutation = gql`
  mutation createArticleParameter($values: CreateArticleParameterInput!) {
    createArticleParameter(values: $values) {
      id
    }
  }
`;

export const UpdateArticleParameterMutation = gql`
  mutation updateArticleParameter($values: UpdateArticleParameterInput!) {
    updateArticleParameter(values: $values) {
      id
    }
  }
`;

export const DeleteArticleParameterMutation = gql`
  mutation deleteArticleParameter($id: ID!) {
    deleteArticleParameter(id: $id)
  }
`;

export interface DeleteArticlePriceTierResponse {
  deleteArticlePriceTier: boolean;
}

export interface DeleteArticlePriceResponse {
  deleteArticlePrice: boolean;
}

export interface DeleteArticleParameterResponse {
  deleteArticleParameter: boolean;
}
